import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import EnhancedAnalysisResult from '../../components/EnhancedAnalysisResult';
import { useAuth } from '../../contexts/AuthContext';
import { adminGetAnalysis, adminSubmitZafyaFeedback } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Same shape EnhancedAnalysisResult already expects (see utils/api.js's
// normalizeAnalysis) — rebuilt here from the raw stored doc since the admin
// endpoint returns the DB record, not the live analyze-endpoint response.
const toConditions = (predictions, roi) =>
  (predictions || []).map((p, i) => {
    const probability = Math.round((p.confidence || 0) * 100);
    const severity = probability >= 70 ? 'Severe' : probability >= 40 ? 'Moderate' : 'Mild';
    return { name: p.label, probability, severity, location: i === 0 ? roi : null };
  });

const AdminAnalysisDetail = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [activeCondition, setActiveCondition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackSent, setFeedbackSent] = useState({}); // turn_id -> rating

  useEffect(() => {
    let cancelled = false;
    adminGetAnalysis(currentUser.token, id)
      .then((data) => {
        if (cancelled) return;
        setAnalysis(data);
        setActiveCondition((data.predictions || [])[0]?.label || null);
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load analysis'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentUser, id]);

  const handleFeedback = async (turnId, rating) => {
    setFeedbackSent((cur) => ({ ...cur, [turnId]: rating }));
    try {
      await adminSubmitZafyaFeedback(currentUser.token, id, { turnId, rating });
    } catch (err) {
      setFeedbackSent((cur) => ({ ...cur, [turnId]: null }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-black text-white">
        <Navbar />
        <main className="flex-grow py-10 page-transition container-custom">
          <p className="text-gray-400">Loading…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-screen flex flex-col bg-black text-white">
        <Navbar />
        <main className="flex-grow py-10 page-transition container-custom">
          <p className="text-red-400">{error || 'Analysis not found'}</p>
        </main>
        <Footer />
      </div>
    );
  }

  const conditions = toConditions(analysis.predictions, analysis.region_of_interest);
  const imageUrl = analysis.image_url?.startsWith('/uploads')
    ? `${API_URL}${analysis.image_url}`
    : analysis.image_url;
  const turns = analysis.zafya_conversation?.turns || [];

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          <Link to="/admin/analyses" className="text-purple-400 hover:text-purple-300 mb-4 inline-block">
            ← Back to analyses
          </Link>
          <div className="flex items-center justify-between mb-6">
            <h1 className="heading-secondary capitalize">{analysis.type} analysis</h1>
            <span className={`px-3 py-1 rounded text-sm ${
              analysis.review_status === 'verified' ? 'bg-green-900/40 text-green-400' :
              analysis.review_status === 'rejected' ? 'bg-red-900/40 text-red-400' :
              'bg-yellow-900/40 text-yellow-400'
            }`}>
              {analysis.review_status}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <EnhancedAnalysisResult
                image={imageUrl}
                conditions={conditions}
                activeCondition={activeCondition}
                onConditionClick={(c) => setActiveCondition(c.name)}
                findings={analysis.predictions?.[0]?.label ? `Leading finding: ${analysis.predictions[0].label}.` : 'No findings.'}
                confidence={conditions[0]?.probability || 0}
                recommendation={(analysis.recommendations || []).join(' ')}
              />

              <div className="mt-6 bg-gray-900 rounded-xl border border-gray-800 p-5 text-sm space-y-2">
                <p><span className="text-gray-400">User:</span> {analysis.user_id}</p>
                <p><span className="text-gray-400">Date:</span> {analysis.date ? new Date(analysis.date).toLocaleString() : '—'}</p>
                {analysis.reviewed_by && (
                  <p><span className="text-gray-400">Reviewed by:</span> {analysis.reviewed_by.name} ({analysis.reviewed_by.email})</p>
                )}
                {analysis.review_notes && (
                  <p><span className="text-gray-400">Review notes:</span> {analysis.review_notes}</p>
                )}
              </div>
            </div>

            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-5">
              <h2 className="heading-subsection mb-4">Zafya conversation</h2>
              {turns.length === 0 ? (
                <p className="text-gray-500 text-sm">No follow-up conversation for this analysis yet.</p>
              ) : (
                <div className="space-y-3">
                  {turns.map((t) => (
                    <div
                      key={t.turn_id}
                      className={`text-sm rounded-lg px-3 py-2 ${
                        t.role === 'user' ? 'bg-purple-900/30 text-purple-100 ml-8' : 'bg-gray-800 text-gray-200 mr-8'
                      }`}
                    >
                      {t.content}
                      {t.role === 'assistant' && (
                        <div className="mt-2 flex items-center gap-3">
                          <button
                            onClick={() => handleFeedback(t.turn_id, 'up')}
                            disabled={!!feedbackSent[t.turn_id] || !!t.feedback}
                            className={`text-xs ${
                              (feedbackSent[t.turn_id] || t.feedback?.rating) === 'up' ? 'text-green-400' : 'text-gray-500 hover:text-gray-300'
                            }`}
                          >
                            👍
                          </button>
                          <button
                            onClick={() => handleFeedback(t.turn_id, 'down')}
                            disabled={!!feedbackSent[t.turn_id] || !!t.feedback}
                            className={`text-xs ${
                              (feedbackSent[t.turn_id] || t.feedback?.rating) === 'down' ? 'text-red-400' : 'text-gray-500 hover:text-gray-300'
                            }`}
                          >
                            👎
                          </button>
                          {t.feedback?.comment && (
                            <span className="text-xs text-gray-500 italic">"{t.feedback.comment}"</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminAnalysisDetail;
